import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const DateSelector = ({ 
  language, 
  selectedDate, 
  onDateChange, 
  label, 
  icon = "📅",
  allowFuture = false 
}) => {
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [allowFutureDates, setAllowFutureDates] = useState(allowFuture);
  const [daysInMonth, setDaysInMonth] = useState([]);
  const [selectionStep, setSelectionStep] = useState('year');

  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 100;
  const maxYear = allowFutureDates ? currentYear + 50 : currentYear;

  // Generate month names based on language
  const getMonthName = (monthNum, lang) => {
    if (lang === 'zh') {
      return `${monthNum}月`;
    } else {
      return new Date(2000, monthNum - 1, 1).toLocaleString('en-US', { month: 'long' });
    }
  };

  // Update days in month when year or month changes
  useEffect(() => {
    if (year && month) {
      const yearNum = parseInt(year);
      const monthNum = parseInt(month);
      const daysCount = new Date(yearNum, monthNum, 0).getDate();
      
      const days = [];
      for (let i = 1; i <= daysCount; i++) {
        const date = new Date(yearNum, monthNum - 1, i);
        if (allowFutureDates || date <= new Date()) {
          days.push(i);
        }
      }
      setDaysInMonth(days);
      
      // Update selection step
      if (selectionStep === 'month') {
        setSelectionStep('day');
      }
    } else {
      setDaysInMonth([]);
    }
  }, [year, month, allowFutureDates]);

  // Update selected date when component props change
  useEffect(() => {
    if (selectedDate) {
      setYear(selectedDate.getFullYear().toString());
      setMonth((selectedDate.getMonth() + 1).toString());
      setDay(selectedDate.getDate().toString());
      setSelectionStep('complete');
    }
  }, [selectedDate]);

  // Update parent component when date changes
  useEffect(() => {
    if (year && month && day) {
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) {
        onDateChange(date);
        setSelectionStep('complete');
      }
    }
  }, [year, month, day, onDateChange]);

  // Update available years when allowFutureDates changes
  useEffect(() => {
    // If current year is not allowed and is selected, reset
    if (!allowFutureDates && year > currentYear) {
      setYear(currentYear.toString());
    }
  }, [allowFutureDates, currentYear, year]);

  // Format the selected date for display
  const formatDate = (date) => {
    if (!date) return language === 'zh' ? '尚未選擇日期' : 'No date selected';
    
    if (language === 'zh') {
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
    } else {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  };

  // Handle allow future dates toggle
  const handleAllowFutureToggle = (e) => {
    setAllowFutureDates(e.target.checked);
  };

  // Handle year change
  const handleYearChange = (e) => {
    setYear(e.target.value);
    if (e.target.value) {
      setSelectionStep('month');
    } else {
      setSelectionStep('year');
      setMonth('');
      setDay('');
    }
  };

  // Handle month change
  const handleMonthChange = (e) => {
    setMonth(e.target.value);
    setDay('');
    if (e.target.value) {
      setSelectionStep('day');
    } else {
      setSelectionStep('month');
    }
  };

  // Handle day change
  const handleDayChange = (e) => {
    setDay(e.target.value);
    if (e.target.value) {
      setSelectionStep('complete');
    } else {
      setSelectionStep('day');
    }
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-750 p-4 rounded-lg border border-love-100 dark:border-love-900 shadow-sm hover:shadow-md transition-shadow"
      whileHover={{ scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300 flex items-center">
        <span className="emoji mr-1">{icon}</span>
        {label}
      </label>

      {/* Selection guidance */}
      <div className="mb-3 text-xs text-gray-500 dark:text-gray-400 flex justify-center">
        <div className={`mx-1 px-2 py-1 rounded-full ${selectionStep === 'year' || selectionStep === 'complete' ? 'bg-love-100 dark:bg-love-900 text-love-700 dark:text-love-300' : ''}`}>
          1. {language === 'zh' ? '選擇年份' : 'Year'}
        </div>
        <div className="mx-1">→</div>
        <div className={`mx-1 px-2 py-1 rounded-full ${selectionStep === 'month' || selectionStep === 'complete' ? 'bg-love-100 dark:bg-love-900 text-love-700 dark:text-love-300' : ''}`}>
          2. {language === 'zh' ? '選擇月份' : 'Month'}
        </div>
        <div className="mx-1">→</div>
        <div className={`mx-1 px-2 py-1 rounded-full ${selectionStep === 'day' || selectionStep === 'complete' ? 'bg-love-100 dark:bg-love-900 text-love-700 dark:text-love-300' : ''}`}>
          3. {language === 'zh' ? '選擇日期' : 'Day'}
        </div>
      </div>
      
      <div className="flex gap-2 mb-2">
        {/* Year Selector - Enhanced UI */}
        <div className="flex-1">
          <div className="text-xs mb-1 font-medium text-gray-600 dark:text-gray-400">
            {language === 'zh' ? '年份' : 'Year'}
          </div>
          <div className="enhanced-select">
            <select
              value={year}
              onChange={handleYearChange}
              aria-label={language === 'zh' ? '年份' : 'Year'}
              className="focus:ring-2 focus:ring-love-400 transition-all"
            >
              <option value="">{language === 'zh' ? '選擇年份' : 'Select Year'}</option>
              {Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i).map(y => (
                <option key={y} value={y}>{y}{language === 'zh' ? '年' : ''}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Month Selector - Enhanced UI with month names */}
        <div className="flex-1">
          <div className="text-xs mb-1 font-medium text-gray-600 dark:text-gray-400">
            {language === 'zh' ? '月份' : 'Month'}
          </div>
          <div className="enhanced-select">
            <select
              value={month}
              onChange={handleMonthChange}
              aria-label={language === 'zh' ? '月份' : 'Month'}
              className="focus:ring-2 focus:ring-love-400 transition-all"
              disabled={!year}
            >
              <option value="">{language === 'zh' ? '選擇月份' : 'Select Month'}</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>
                  {getMonthName(m, language)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Day Selector - Fixed to properly display days */}
        <div className="flex-1">
          <div className="text-xs mb-1 font-medium text-gray-600 dark:text-gray-400">
            {language === 'zh' ? '日期' : 'Day'}
          </div>
          <div className="enhanced-select">
            <select
              value={day}
              onChange={handleDayChange}
              aria-label={language === 'zh' ? '日期' : 'Day'}
              disabled={!year || !month || daysInMonth.length === 0}
              className="focus:ring-2 focus:ring-love-400 transition-all"
            >
              <option value="">{language === 'zh' ? '選擇日期' : 'Select Day'}</option>
              {daysInMonth.map(d => (
                <option key={d} value={d}>{d}{language === 'zh' ? '日' : ''}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Date Display - Enhanced with animation */}
      <motion.div 
        className="mt-2 p-2 border-2 border-love-200 dark:border-love-800 rounded-lg bg-love-50 dark:bg-love-900/20 text-center text-sm font-medium"
        initial={{ opacity: 0.8 }}
        animate={{ 
          opacity: selectedDate ? 1 : 0.8,
          scale: selectedDate ? 1 : 0.98
        }}
        transition={{ duration: 0.3 }}
      >
        {formatDate(selectedDate)}
      </motion.div>

      {/* Future Date Toggle - Enhanced UI */}
      <div className="flex items-center mt-2">
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={allowFutureDates}
            onChange={handleAllowFutureToggle}
          />
          <span className="toggle-slider"></span>
        </label>
        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
          {language === 'zh' ? '允許選擇未來日期' : 'Allow future dates'}
        </span>
      </div>
    </motion.div>
  );
};

export default DateSelector;